import React, { useState, useMemo } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';
import {
  TrendingUp, TrendingDown, Eye, Users, Clock, ArrowUpRight,
  Monitor, Smartphone, Tablet, Globe, Search, Share2, Link as LinkIcon,
  ChevronDown, ChevronRight, Info, ExternalLink, MousePointerClick, BarChart3,
  MapPin, ArrowUp, ArrowDown, Minus
} from 'lucide-react';

/* ── Mock Data Generator ── */
const generateDailyData = (days) => {
  const data = [];
  const now = new Date();
  const baseVisitors = days <= 7 ? 85 : days <= 30 ? 72 : days <= 90 ? 60 : 55;
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const dayOfWeek = d.getDay();
    const weekendDrop = (dayOfWeek === 0 || dayOfWeek === 6) ? 0.6 : 1;
    const noise = 0.7 + Math.random() * 0.6;
    const trend = 1 + (days - i) * 0.004;
    const visitors = Math.round(baseVisitors * weekendDrop * noise * trend);
    const sessions = Math.round(visitors * (1.1 + Math.random() * 0.3));
    const pageViews = Math.round(sessions * (2.2 + Math.random() * 1.5));
    data.push({
      date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      fullDate: d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      visitors,
      sessions,
      pageViews,
    });
  }
  return data;
};

const getTopPages = (range) => {
  const multiplier = range <= 7 ? 0.23 : range <= 30 ? 1 : range <= 90 ? 2.9 : 12;
  return [
    { page: '/', title: 'Home', views: Math.round(4821 * multiplier), pct: 32.1 },
    { page: '/products', title: '2026 Truck Campers', views: Math.round(2913 * multiplier), pct: 19.4 },
    { page: '/gallery', title: 'Photo Gallery', views: Math.round(1847 * multiplier), pct: 12.3 },
    { page: '/kei-truck', title: 'Kei Truck Box', views: Math.round(1206 * multiplier), pct: 8.0 },
    { page: '/contact', title: 'Contact Us', views: Math.round(987 * multiplier), pct: 6.6 },
    { page: '/media', title: 'Media & Press', views: Math.round(743 * multiplier), pct: 4.9 },
    { page: '/why', title: 'Why Total Composites', views: Math.round(612 * multiplier), pct: 4.1 },
    { page: '/order', title: 'Order Form', views: Math.round(489 * multiplier), pct: 3.3 },
  ];
};

const getTrafficSources = (range) => {
  if (range <= 7) return [
    { name: 'Direct', value: 42, color: '#E8520A' },
    { name: 'Organic Search', value: 28, color: '#2563eb' },
    { name: 'Social', value: 19, color: '#7c3aed' },
    { name: 'Referral', value: 7, color: '#16a34a' },
    { name: 'Email', value: 4, color: '#d97706' },
  ];
  if (range <= 30) return [
    { name: 'Direct', value: 38, color: '#E8520A' },
    { name: 'Organic Search', value: 31, color: '#2563eb' },
    { name: 'Social', value: 18, color: '#7c3aed' },
    { name: 'Referral', value: 9, color: '#16a34a' },
    { name: 'Email', value: 4, color: '#d97706' },
  ];
  if (range <= 90) return [
    { name: 'Direct', value: 35, color: '#E8520A' },
    { name: 'Organic Search', value: 34, color: '#2563eb' },
    { name: 'Social', value: 17, color: '#7c3aed' },
    { name: 'Referral', value: 10, color: '#16a34a' },
    { name: 'Email', value: 4, color: '#d97706' },
  ];
  return [
    { name: 'Direct', value: 33, color: '#E8520A' },
    { name: 'Organic Search', value: 36, color: '#2563eb' },
    { name: 'Social', value: 16, color: '#7c3aed' },
    { name: 'Referral', value: 11, color: '#16a34a' },
    { name: 'Email', value: 4, color: '#d97706' },
  ];
};

const getDevices = (range) => {
  if (range <= 7) return [
    { name: 'Desktop', value: 55, icon: Monitor, color: '#374151' },
    { name: 'Mobile', value: 38, icon: Smartphone, color: '#E8520A' },
    { name: 'Tablet', value: 7, icon: Tablet, color: '#6b7280' },
  ];
  if (range <= 30) return [
    { name: 'Desktop', value: 58, icon: Monitor, color: '#374151' },
    { name: 'Mobile', value: 35, icon: Smartphone, color: '#E8520A' },
    { name: 'Tablet', value: 7, icon: Tablet, color: '#6b7280' },
  ];
  if (range <= 90) return [
    { name: 'Desktop', value: 60, icon: Monitor, color: '#374151' },
    { name: 'Mobile', value: 33, icon: Smartphone, color: '#E8520A' },
    { name: 'Tablet', value: 7, icon: Tablet, color: '#6b7280' },
  ];
  return [
    { name: 'Desktop', value: 61, icon: Monitor, color: '#374151' },
    { name: 'Mobile', value: 32, icon: Smartphone, color: '#E8520A' },
    { name: 'Tablet', value: 7, icon: Tablet, color: '#6b7280' },
  ];
};

const getCountries = (range) => {
  const multiplier = range <= 7 ? 0.23 : range <= 30 ? 1 : range <= 90 ? 2.9 : 12;
  return [
    { name: 'Canada', flag: '\u{1F1E8}\u{1F1E6}', sessions: Math.round(4120 * multiplier), pct: 41.2 },
    { name: 'United States', flag: '\u{1F1FA}\u{1F1F8}', sessions: Math.round(3180 * multiplier), pct: 31.8 },
    { name: 'Australia', flag: '\u{1F1E6}\u{1F1FA}', sessions: Math.round(890 * multiplier), pct: 8.9 },
    { name: 'United Kingdom', flag: '\u{1F1EC}\u{1F1E7}', sessions: Math.round(620 * multiplier), pct: 6.2 },
    { name: 'Germany', flag: '\u{1F1E9}\u{1F1EA}', sessions: Math.round(410 * multiplier), pct: 4.1 },
    { name: 'Other', flag: '\u{1F30D}', sessions: Math.round(780 * multiplier), pct: 7.8 },
  ];
};

/* ── Search & Keyword Data ── */
const getSearchKeywords = (range) => {
  const multiplier = range <= 7 ? 0.23 : range <= 30 ? 1 : range <= 90 ? 2.9 : 12;
  return [
    { keyword: 'truck camper composite', clicks: Math.round(892 * multiplier), impressions: Math.round(12400 * multiplier), ctr: 7.2, position: 2.1, change: 14.2 },
    { keyword: 'expedition camper shell', clicks: Math.round(674 * multiplier), impressions: Math.round(9800 * multiplier), ctr: 6.9, position: 3.4, change: 8.6 },
    { keyword: 'total composites camper', clicks: Math.round(521 * multiplier), impressions: Math.round(4200 * multiplier), ctr: 12.4, position: 1.2, change: 22.1 },
    { keyword: 'composite truck body', clicks: Math.round(438 * multiplier), impressions: Math.round(7600 * multiplier), ctr: 5.8, position: 4.7, change: -3.4 },
    { keyword: 'lightweight truck camper', clicks: Math.round(367 * multiplier), impressions: Math.round(8900 * multiplier), ctr: 4.1, position: 5.3, change: 5.8 },
    { keyword: 'kei truck camper box', clicks: Math.round(298 * multiplier), impressions: Math.round(5100 * multiplier), ctr: 5.8, position: 3.8, change: 31.5 },
    { keyword: 'overlanding camper canada', clicks: Math.round(245 * multiplier), impressions: Math.round(6300 * multiplier), ctr: 3.9, position: 6.1, change: -7.2 },
    { keyword: 'expedition kit camper build', clicks: Math.round(189 * multiplier), impressions: Math.round(4700 * multiplier), ctr: 4.0, position: 5.9, change: 0 },
    { keyword: 'custom expedition vehicle', clicks: Math.round(156 * multiplier), impressions: Math.round(5500 * multiplier), ctr: 2.8, position: 8.2, change: -12.1 },
    { keyword: '4 season truck camper', clicks: Math.round(134 * multiplier), impressions: Math.round(3200 * multiplier), ctr: 4.2, position: 4.5, change: 18.9 },
  ];
};

const getSearchEngines = (range) => {
  if (range <= 7) return [
    { name: 'Google', value: 72, color: '#4285F4' },
    { name: 'Bing', value: 14, color: '#00897B' },
    { name: 'DuckDuckGo', value: 8, color: '#DE5833' },
    { name: 'Yahoo', value: 4, color: '#6001D2' },
    { name: 'Other', value: 2, color: '#6b7280' },
  ];
  if (range <= 30) return [
    { name: 'Google', value: 74, color: '#4285F4' },
    { name: 'Bing', value: 13, color: '#00897B' },
    { name: 'DuckDuckGo', value: 7, color: '#DE5833' },
    { name: 'Yahoo', value: 4, color: '#6001D2' },
    { name: 'Other', value: 2, color: '#6b7280' },
  ];
  if (range <= 90) return [
    { name: 'Google', value: 75, color: '#4285F4' },
    { name: 'Bing', value: 12, color: '#00897B' },
    { name: 'DuckDuckGo', value: 7, color: '#DE5833' },
    { name: 'Yahoo', value: 4, color: '#6001D2' },
    { name: 'Other', value: 2, color: '#6b7280' },
  ];
  return [
    { name: 'Google', value: 76, color: '#4285F4' },
    { name: 'Bing', value: 12, color: '#00897B' },
    { name: 'DuckDuckGo', value: 6, color: '#DE5833' },
    { name: 'Yahoo', value: 4, color: '#6001D2' },
    { name: 'Other', value: 2, color: '#6b7280' },
  ];
};

const getReferralSites = (range) => {
  const multiplier = range <= 7 ? 0.23 : range <= 30 ? 1 : range <= 90 ? 2.9 : 12;
  return [
    { site: 'facebook.com', sessions: Math.round(1240 * multiplier), pct: 28.4, urls: [
      { url: 'facebook.com/groups/overlanding', clicks: Math.round(480 * multiplier), firstSeen: '2024-09-12', lastClick: '2026-03-31', bounceRate: 38.2, avgTime: '3m 12s', landingPage: '/products' },
      { url: 'facebook.com/totalcomposites', clicks: Math.round(390 * multiplier), firstSeen: '2024-06-01', lastClick: '2026-04-01', bounceRate: 31.5, avgTime: '4m 08s', landingPage: '/' },
      { url: 'facebook.com/marketplace/item/tc2026', clicks: Math.round(210 * multiplier), firstSeen: '2025-11-20', lastClick: '2026-03-28', bounceRate: 44.1, avgTime: '2m 45s', landingPage: '/order' },
      { url: 'facebook.com/groups/truckcampers', clicks: Math.round(160 * multiplier), firstSeen: '2025-01-15', lastClick: '2026-03-30', bounceRate: 42.8, avgTime: '2m 22s', landingPage: '/gallery' },
    ]},
    { site: 'instagram.com', sessions: Math.round(890 * multiplier), pct: 20.4, urls: [
      { url: 'instagram.com/totalcomposites', clicks: Math.round(520 * multiplier), firstSeen: '2024-03-10', lastClick: '2026-04-01', bounceRate: 35.0, avgTime: '3m 40s', landingPage: '/' },
      { url: 'instagram.com/p/expedition_build', clicks: Math.round(220 * multiplier), firstSeen: '2025-08-05', lastClick: '2026-03-29', bounceRate: 28.6, avgTime: '5m 15s', landingPage: '/gallery' },
      { url: 'instagram.com/reel/camper_tour', clicks: Math.round(150 * multiplier), firstSeen: '2025-12-01', lastClick: '2026-03-31', bounceRate: 40.2, avgTime: '2m 58s', landingPage: '/products' },
    ]},
    { site: 'expeditionportal.com', sessions: Math.round(620 * multiplier), pct: 14.2, urls: [
      { url: 'expeditionportal.com/forum/threads/total-composites-review', clicks: Math.round(310 * multiplier), firstSeen: '2024-11-08', lastClick: '2026-03-30', bounceRate: 22.4, avgTime: '6m 32s', landingPage: '/why' },
      { url: 'expeditionportal.com/forum/threads/composite-vs-aluminum', clicks: Math.round(180 * multiplier), firstSeen: '2025-02-14', lastClick: '2026-03-28', bounceRate: 26.1, avgTime: '5m 48s', landingPage: '/products' },
      { url: 'expeditionportal.com/resources/camper-builds', clicks: Math.round(130 * multiplier), firstSeen: '2025-06-20', lastClick: '2026-03-25', bounceRate: 30.5, avgTime: '4m 20s', landingPage: '/gallery' },
    ]},
    { site: 'youtube.com', sessions: Math.round(510 * multiplier), pct: 11.7, urls: [
      { url: 'youtube.com/watch?v=tc_walkaround', clicks: Math.round(280 * multiplier), firstSeen: '2025-04-18', lastClick: '2026-04-01', bounceRate: 25.8, avgTime: '7m 05s', landingPage: '/products' },
      { url: 'youtube.com/watch?v=kei_truck_build', clicks: Math.round(140 * multiplier), firstSeen: '2025-09-22', lastClick: '2026-03-29', bounceRate: 30.2, avgTime: '5m 12s', landingPage: '/kei-truck' },
      { url: 'youtube.com/@overlanding_channel', clicks: Math.round(90 * multiplier), firstSeen: '2025-07-10', lastClick: '2026-03-27', bounceRate: 38.9, avgTime: '3m 50s', landingPage: '/' },
    ]},
    { site: 'reddit.com/r/overlanding', sessions: Math.round(380 * multiplier), pct: 8.7, urls: [
      { url: 'reddit.com/r/overlanding/comments/tc_camper', clicks: Math.round(210 * multiplier), firstSeen: '2025-05-03', lastClick: '2026-03-31', bounceRate: 33.4, avgTime: '4m 18s', landingPage: '/products' },
      { url: 'reddit.com/r/truckcampers/comments/composite', clicks: Math.round(110 * multiplier), firstSeen: '2025-10-12', lastClick: '2026-03-28', bounceRate: 36.7, avgTime: '3m 55s', landingPage: '/why' },
      { url: 'reddit.com/r/vandwellers/comments/kei_box', clicks: Math.round(60 * multiplier), firstSeen: '2026-01-08', lastClick: '2026-03-26', bounceRate: 42.0, avgTime: '2m 30s', landingPage: '/kei-truck' },
    ]},
    { site: 'thecampervan.co', sessions: Math.round(290 * multiplier), pct: 6.6, urls: [
      { url: 'thecampervan.co/reviews/total-composites-2026', clicks: Math.round(180 * multiplier), firstSeen: '2025-11-15', lastClick: '2026-03-30', bounceRate: 20.1, avgTime: '6m 45s', landingPage: '/products' },
      { url: 'thecampervan.co/best-truck-campers-2026', clicks: Math.round(110 * multiplier), firstSeen: '2026-01-20', lastClick: '2026-03-29', bounceRate: 24.8, avgTime: '5m 30s', landingPage: '/' },
    ]},
    { site: 'truckcampermagazine.com', sessions: Math.round(210 * multiplier), pct: 4.8, urls: [
      { url: 'truckcampermagazine.com/news/total-composites-launch', clicks: Math.round(130 * multiplier), firstSeen: '2025-08-01', lastClick: '2026-03-28', bounceRate: 18.5, avgTime: '7m 20s', landingPage: '/media' },
      { url: 'truckcampermagazine.com/reviews/composite-shells', clicks: Math.round(80 * multiplier), firstSeen: '2025-12-10', lastClick: '2026-03-25', bounceRate: 22.3, avgTime: '6m 10s', landingPage: '/products' },
    ]},
    { site: 'pinterest.com', sessions: Math.round(220 * multiplier), pct: 5.0, urls: [
      { url: 'pinterest.com/pin/truck-camper-interior', clicks: Math.round(120 * multiplier), firstSeen: '2025-03-22', lastClick: '2026-03-31', bounceRate: 48.2, avgTime: '1m 55s', landingPage: '/gallery' },
      { url: 'pinterest.com/pin/expedition-setup', clicks: Math.round(65 * multiplier), firstSeen: '2025-07-14', lastClick: '2026-03-29', bounceRate: 45.0, avgTime: '2m 10s', landingPage: '/gallery' },
      { url: 'pinterest.com/totalcomposites', clicks: Math.round(35 * multiplier), firstSeen: '2025-10-05', lastClick: '2026-03-27', bounceRate: 40.5, avgTime: '2m 40s', landingPage: '/' },
    ]},
  ];
};

const SOURCE_ICONS = { 'Direct': Globe, 'Organic Search': Search, 'Social': Share2, 'Referral': LinkIcon, 'Email': ArrowUpRight };

/* ── Region Configuration (Continent → Country → State/Province) ── */
const CONTINENTS = [
  { id: 'global', label: 'Global', icon: '\u{1F30D}' },
  { id: 'north-america', label: 'North America', icon: '\u{1F30E}', countries: [
    { id: 'canada', label: 'Canada', flag: '\u{1F1E8}\u{1F1E6}', regions: [
      { id: 'ca-ab', label: 'Alberta' },
      { id: 'ca-bc', label: 'British Columbia' },
      { id: 'ca-mb', label: 'Manitoba' },
      { id: 'ca-nb', label: 'New Brunswick' },
      { id: 'ca-nl', label: 'Newfoundland & Labrador' },
      { id: 'ca-ns', label: 'Nova Scotia' },
      { id: 'ca-nt', label: 'Northwest Territories' },
      { id: 'ca-nu', label: 'Nunavut' },
      { id: 'ca-on', label: 'Ontario' },
      { id: 'ca-pe', label: 'Prince Edward Island' },
      { id: 'ca-qc', label: 'Qu\u00e9bec' },
      { id: 'ca-sk', label: 'Saskatchewan' },
      { id: 'ca-yt', label: 'Yukon' },
    ]},
    { id: 'usa', label: 'United States', flag: '\u{1F1FA}\u{1F1F8}', regions: [
      { id: 'us-al', label: 'Alabama' },
      { id: 'us-ak', label: 'Alaska' },
      { id: 'us-az', label: 'Arizona' },
      { id: 'us-ar', label: 'Arkansas' },
      { id: 'us-ca', label: 'California' },
      { id: 'us-co', label: 'Colorado' },
      { id: 'us-ct', label: 'Connecticut' },
      { id: 'us-de', label: 'Delaware' },
      { id: 'us-dc', label: 'District of Columbia' },
      { id: 'us-fl', label: 'Florida' },
      { id: 'us-ga', label: 'Georgia' },
      { id: 'us-hi', label: 'Hawaii' },
      { id: 'us-id', label: 'Idaho' },
      { id: 'us-il', label: 'Illinois' },
      { id: 'us-in', label: 'Indiana' },
      { id: 'us-ia', label: 'Iowa' },
      { id: 'us-ks', label: 'Kansas' },
      { id: 'us-ky', label: 'Kentucky' },
      { id: 'us-la', label: 'Louisiana' },
      { id: 'us-me', label: 'Maine' },
      { id: 'us-md', label: 'Maryland' },
      { id: 'us-ma', label: 'Massachusetts' },
      { id: 'us-mi', label: 'Michigan' },
      { id: 'us-mn', label: 'Minnesota' },
      { id: 'us-ms', label: 'Mississippi' },
      { id: 'us-mo', label: 'Missouri' },
      { id: 'us-mt', label: 'Montana' },
      { id: 'us-ne', label: 'Nebraska' },
      { id: 'us-nv', label: 'Nevada' },
      { id: 'us-nh', label: 'New Hampshire' },
      { id: 'us-nj', label: 'New Jersey' },
      { id: 'us-nm', label: 'New Mexico' },
      { id: 'us-ny', label: 'New York' },
      { id: 'us-nc', label: 'North Carolina' },
      { id: 'us-nd', label: 'North Dakota' },
      { id: 'us-oh', label: 'Ohio' },
      { id: 'us-ok', label: 'Oklahoma' },
      { id: 'us-or', label: 'Oregon' },
      { id: 'us-pa', label: 'Pennsylvania' },
      { id: 'us-ri', label: 'Rhode Island' },
      { id: 'us-sc', label: 'South Carolina' },
      { id: 'us-sd', label: 'South Dakota' },
      { id: 'us-tn', label: 'Tennessee' },
      { id: 'us-tx', label: 'Texas' },
      { id: 'us-ut', label: 'Utah' },
      { id: 'us-vt', label: 'Vermont' },
      { id: 'us-va', label: 'Virginia' },
      { id: 'us-wa', label: 'Washington' },
      { id: 'us-wv', label: 'West Virginia' },
      { id: 'us-wi', label: 'Wisconsin' },
      { id: 'us-wy', label: 'Wyoming' },
    ]},
    { id: 'mexico', label: 'Mexico', flag: '\u{1F1F2}\u{1F1FD}' },
  ]},
  { id: 'europe', label: 'Europe', icon: '\u{1F30D}', countries: [
    { id: 'uk', label: 'United Kingdom', flag: '\u{1F1EC}\u{1F1E7}', regions: [
      { id: 'uk-eng', label: 'England' },
      { id: 'uk-sct', label: 'Scotland' },
      { id: 'uk-wls', label: 'Wales' },
      { id: 'uk-nir', label: 'Northern Ireland' },
    ]},
    { id: 'germany', label: 'Germany', flag: '\u{1F1E9}\u{1F1EA}' },
    { id: 'france', label: 'France', flag: '\u{1F1EB}\u{1F1F7}' },
    { id: 'netherlands', label: 'Netherlands', flag: '\u{1F1F3}\u{1F1F1}' },
    { id: 'switzerland', label: 'Switzerland', flag: '\u{1F1E8}\u{1F1ED}' },
    { id: 'sweden', label: 'Sweden', flag: '\u{1F1F8}\u{1F1EA}' },
    { id: 'norway', label: 'Norway', flag: '\u{1F1F3}\u{1F1F4}' },
    { id: 'italy', label: 'Italy', flag: '\u{1F1EE}\u{1F1F9}' },
    { id: 'spain', label: 'Spain', flag: '\u{1F1EA}\u{1F1F8}' },
  ]},
  { id: 'oceania', label: 'Oceania', icon: '\u{1F30F}', countries: [
    { id: 'australia', label: 'Australia', flag: '\u{1F1E6}\u{1F1FA}', regions: [
      { id: 'au-nsw', label: 'New South Wales' },
      { id: 'au-vic', label: 'Victoria' },
      { id: 'au-qld', label: 'Queensland' },
      { id: 'au-wa', label: 'Western Australia' },
      { id: 'au-sa', label: 'South Australia' },
      { id: 'au-tas', label: 'Tasmania' },
    ]},
    { id: 'new-zealand', label: 'New Zealand', flag: '\u{1F1F3}\u{1F1FF}' },
  ]},
  { id: 'asia', label: 'Asia', icon: '\u{1F30F}', countries: [
    { id: 'japan', label: 'Japan', flag: '\u{1F1EF}\u{1F1F5}' },
    { id: 'south-korea', label: 'South Korea', flag: '\u{1F1F0}\u{1F1F7}' },
  ]},
  { id: 'south-america', label: 'South America', icon: '\u{1F30E}', countries: [
    { id: 'brazil', label: 'Brazil', flag: '\u{1F1E7}\u{1F1F7}' },
    { id: 'argentina', label: 'Argentina', flag: '\u{1F1E6}\u{1F1F7}' },
    { id: 'chile', label: 'Chile', flag: '\u{1F1E8}\u{1F1F1}' },
  ]},
  { id: 'africa', label: 'Africa', icon: '\u{1F30D}', countries: [
    { id: 'south-africa', label: 'South Africa', flag: '\u{1F1FF}\u{1F1E6}' },
  ]},
];

const REGION_MULTIPLIERS = {
  'global': 1,
  // Continents
  'north-america': 0.74, 'europe': 0.14, 'oceania': 0.095, 'asia': 0.012, 'south-america': 0.008, 'africa': 0.005,
  // Canada (41% total)
  'canada': 0.41,
  'ca-on': 0.14, 'ca-qc': 0.12, 'ca-bc': 0.07, 'ca-ab': 0.05, 'ca-mb': 0.01,
  'ca-sk': 0.008, 'ca-ns': 0.006, 'ca-nb': 0.004, 'ca-nl': 0.003, 'ca-pe': 0.0015,
  'ca-nt': 0.0005, 'ca-nu': 0.0002, 'ca-yt': 0.0003,
  // USA (32% total)
  'usa': 0.32,
  'us-ca': 0.042, 'us-co': 0.032, 'us-tx': 0.028, 'us-wa': 0.022, 'us-or': 0.019,
  'us-mt': 0.017, 'us-ut': 0.015, 'us-az': 0.014, 'us-ny': 0.012, 'us-fl': 0.011,
  'us-id': 0.010, 'us-nv': 0.009, 'us-nm': 0.008, 'us-wy': 0.008, 'us-nc': 0.007,
  'us-va': 0.007, 'us-ga': 0.006, 'us-pa': 0.006, 'us-oh': 0.005, 'us-mi': 0.005,
  'us-il': 0.005, 'us-mn': 0.005, 'us-wi': 0.004, 'us-tn': 0.004, 'us-mo': 0.004,
  'us-ma': 0.004, 'us-md': 0.003, 'us-in': 0.003, 'us-sc': 0.003, 'us-al': 0.003,
  'us-ak': 0.005, 'us-sd': 0.003, 'us-nd': 0.003, 'us-me': 0.003, 'us-nh': 0.003,
  'us-vt': 0.003, 'us-ia': 0.002, 'us-ne': 0.002, 'us-ks': 0.002, 'us-ok': 0.002,
  'us-ar': 0.002, 'us-la': 0.002, 'us-ms': 0.001, 'us-ky': 0.002, 'us-wv': 0.001,
  'us-ct': 0.002, 'us-nj': 0.003, 'us-de': 0.001, 'us-ri': 0.001, 'us-hi': 0.002,
  'us-dc': 0.001,
  // Mexico
  'mexico': 0.01,
  // Europe
  'uk': 0.062, 'uk-eng': 0.048, 'uk-sct': 0.008, 'uk-wls': 0.003, 'uk-nir': 0.003,
  'germany': 0.041, 'france': 0.028, 'netherlands': 0.012, 'switzerland': 0.010,
  'sweden': 0.008, 'norway': 0.009, 'italy': 0.006, 'spain': 0.005,
  // Oceania
  'australia': 0.089, 'au-nsw': 0.032, 'au-vic': 0.025, 'au-qld': 0.018, 'au-wa': 0.007, 'au-sa': 0.004, 'au-tas': 0.003,
  'new-zealand': 0.006,
  // Asia
  'japan': 0.008, 'south-korea': 0.004,
  // South America
  'brazil': 0.004, 'argentina': 0.002, 'chile': 0.002,
  // Africa
  'south-africa': 0.005,
};

const getRegionMultiplier = (key) => REGION_MULTIPLIERS[key] || 0.005;

/* ── Resolve label from region key ── */
const getRegionLabel = (key) => {
  if (key === 'global') return 'Global';
  for (const cont of CONTINENTS) {
    if (cont.id === key) return cont.label;
    if (cont.countries) {
      for (const c of cont.countries) {
        if (c.id === key) return c.label;
        if (c.regions) {
          const r = c.regions.find(r => r.id === key);
          if (r) return r.label;
        }
      }
    }
  }
  return key;
};

const getRegionFlag = (key) => {
  if (key === 'global') return '\u{1F30D}';
  for (const cont of CONTINENTS) {
    if (cont.id === key) return cont.icon;
    if (cont.countries) {
      for (const c of cont.countries) {
        if (c.id === key) return c.flag;
        if (c.regions) {
          if (c.regions.some(r => r.id === key)) return c.flag;
        }
      }
    }
  }
  return '\u{1F30D}';
};

/* ── Region Selector Component (3-level) ── */
const RegionSelector = ({ region, onRegionChange }) => {
  const [open, setOpen] = useState(false);
  const [expandedContinent, setExpandedContinent] = useState(null);
  const [expandedCountry, setExpandedCountry] = useState(null);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const displayLabel = `${getRegionFlag(region)} ${getRegionLabel(region)}`;

  const selectRegion = (key) => {
    onRegionChange(key);
    setOpen(false);
    setExpandedContinent(null);
    setExpandedCountry(null);
  };

  return (
    <div className="relative" ref={dropdownRef} data-testid="region-selector">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-700 hover:border-gray-300 transition-all"
      >
        <MapPin size={12} style={{ color: '#E8520A' }} />
        <span className="max-w-[140px] truncate">{displayLabel}</span>
        <ChevronDown size={12} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-gray-200 w-64 max-h-96 overflow-y-auto z-50" data-testid="region-dropdown">
          {CONTINENTS.map(cont => (
            <div key={cont.id}>
              {/* Continent / Global row */}
              <button
                onClick={() => {
                  if (cont.countries) {
                    setExpandedContinent(expandedContinent === cont.id ? null : cont.id);
                    setExpandedCountry(null);
                  } else {
                    selectRegion(cont.id);
                  }
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-bold transition-colors ${
                  region === cont.id ? 'bg-orange-50 text-orange-700' : 'text-gray-800 hover:bg-gray-50'
                }`}
                data-testid={`region-opt-${cont.id}`}
              >
                <span className="text-sm">{cont.icon}</span>
                <span className="flex-1 text-left">{cont.label}</span>
                {cont.countries && (
                  <ChevronRight size={12} className={`text-gray-400 transition-transform ${expandedContinent === cont.id ? 'rotate-90' : ''}`} />
                )}
              </button>

              {/* Level 2: Countries */}
              {cont.countries && expandedContinent === cont.id && (
                <div className="border-l-2 border-orange-100 ml-4">
                  {/* Select entire continent */}
                  <button
                    onClick={() => selectRegion(cont.id)}
                    className={`w-full text-left pl-4 pr-3 py-1.5 text-[11px] font-semibold transition-colors ${
                      region === cont.id ? 'text-orange-700 bg-orange-50' : 'text-gray-500 hover:text-orange-600 hover:bg-orange-50/50'
                    }`}
                  >
                    All {cont.label}
                  </button>
                  {cont.countries.map(country => (
                    <div key={country.id}>
                      <button
                        onClick={() => {
                          if (country.regions) {
                            setExpandedCountry(expandedCountry === country.id ? null : country.id);
                          } else {
                            selectRegion(country.id);
                          }
                        }}
                        className={`w-full flex items-center gap-2 pl-4 pr-3 py-1.5 text-[11px] font-semibold transition-colors ${
                          region === country.id ? 'text-orange-700 bg-orange-50' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        data-testid={`region-opt-${country.id}`}
                      >
                        <span className="text-xs">{country.flag}</span>
                        <span className="flex-1 text-left">{country.label}</span>
                        {country.regions && (
                          <ChevronRight size={10} className={`text-gray-400 transition-transform ${expandedCountry === country.id ? 'rotate-90' : ''}`} />
                        )}
                      </button>

                      {/* Level 3: States / Provinces / Regions */}
                      {country.regions && expandedCountry === country.id && (
                        <div className="border-l-2 border-gray-100 ml-6 max-h-48 overflow-y-auto">
                          {/* Select entire country */}
                          <button
                            onClick={() => selectRegion(country.id)}
                            className={`w-full text-left pl-4 pr-3 py-1 text-[10px] font-semibold transition-colors ${
                              region === country.id ? 'text-orange-700 bg-orange-50' : 'text-gray-500 hover:text-orange-600 hover:bg-orange-50/50'
                            }`}
                          >
                            All {country.label}
                          </button>
                          {country.regions.map(sub => (
                            <button
                              key={sub.id}
                              onClick={() => selectRegion(sub.id)}
                              className={`w-full text-left pl-4 pr-3 py-1 text-[10px] font-medium transition-colors ${
                                region === sub.id ? 'text-orange-700 bg-orange-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                              }`}
                              data-testid={`region-opt-${sub.id}`}
                            >
                              {sub.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Custom Tooltip ── */
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl border border-zinc-700">
      <p className="font-semibold mb-1">{payload[0]?.payload?.fullDate || label}</p>
      {payload.map((p, i) => (
        <p key={p.name || p.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          {p.name}: <strong>{p.value.toLocaleString()}</strong>
        </p>
      ))}
    </div>
  );
};

/* ── KPI Card ── */
const KpiCard = ({ label, value, change, icon: Icon, suffix = '' }) => {
  const isUp = change >= 0;
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-3 sm:p-5 hover:shadow-md transition-shadow" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-2 sm:mb-3">
        <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-gray-400">{label}</span>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FFF0E8' }}>
          <Icon size={16} style={{ color: '#E8520A' }} />
        </div>
      </div>
      <p className="text-lg sm:text-2xl font-black text-gray-900">{value}{suffix}</p>
      <div className="flex items-center gap-1 mt-1">
        {isUp ? <TrendingUp size={12} className="text-green-500" /> : <TrendingDown size={12} className="text-red-500" />}
        <span className={`text-xs font-bold ${isUp ? 'text-green-600' : 'text-red-600'}`}>
          {isUp ? '+' : ''}{change}%
        </span>
        <span className="text-xs text-gray-400 ml-1">vs prior period</span>
      </div>
    </div>
  );
};

/* ── URL Detail Popup ── */
const UrlDetailPopup = ({ urlData, onClose }) => {
  if (!urlData) return null;
  const stats = [
    { label: 'Total Clicks', value: urlData.clicks.toLocaleString(), color: '#E8520A' },
    { label: 'First Seen', value: urlData.firstSeen },
    { label: 'Last Click', value: urlData.lastClick },
    { label: 'Bounce Rate', value: `${urlData.bounceRate}%`, color: urlData.bounceRate > 40 ? '#ef4444' : '#22c55e' },
    { label: 'Avg. Time on Site', value: urlData.avgTime },
    { label: 'Landing Page', value: urlData.landingPage },
  ];

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose} data-testid="url-detail-popup">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100" style={{ backgroundColor: '#1a1a1a' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <ExternalLink size={14} className="text-orange-400 flex-shrink-0" />
              <span className="text-xs font-bold text-white truncate">{urlData.url}</span>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors ml-2 flex-shrink-0" data-testid="url-popup-close">
              <span className="text-lg leading-none">&times;</span>
            </button>
          </div>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-2 gap-4">
            {stats.map(s => (
              <div key={s.label} className="bg-gray-50 rounded-lg p-3">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">{s.label}</p>
                <p className="text-sm font-black" style={{ color: s.color || '#111827' }}>{s.value}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2">
            <MousePointerClick size={12} className="text-gray-400" />
            <span className="text-[10px] text-gray-400">
              Active for <strong className="text-gray-600">
                {Math.round((new Date(urlData.lastClick) - new Date(urlData.firstSeen)) / (1000 * 60 * 60 * 24))} days
              </strong>
              {' · '}~<strong className="text-gray-600">
                {(urlData.clicks / Math.max(1, Math.round((new Date(urlData.lastClick) - new Date(urlData.firstSeen)) / (1000 * 60 * 60 * 24)))).toFixed(1)}
              </strong> clicks/day
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Referral Site Row (expandable) ── */
const ReferralSiteRow = ({ site, index }) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedUrl, setSelectedUrl] = useState(null);

  return (
    <>
      <div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center gap-3 py-2 px-1 rounded-lg hover:bg-white/60 transition-colors group"
          data-testid={`referral-row-${site.site.replace(/[/.]/g, '-')}`}
        >
          <span className="w-5 text-xs font-bold text-gray-300 text-right">{index + 1}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <ChevronRight size={10} className={`text-gray-400 transition-transform flex-shrink-0 ${expanded ? 'rotate-90' : ''}`} />
              <LinkIcon size={10} className="text-gray-400 flex-shrink-0" />
              <span className="text-xs font-semibold text-gray-800 truncate">{site.site}</span>
              <span className="text-[9px] text-gray-400 hidden group-hover:inline">({site.urls?.length || 0} URLs)</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1 mt-1 ml-5">
              <div
                className="h-1 rounded-full transition-all"
                style={{ width: `${site.pct}%`, backgroundColor: '#E8520A', opacity: 1 - index * 0.08 }}
              />
            </div>
          </div>
          <span className="text-xs font-bold text-gray-600 w-12 text-right">{site.sessions.toLocaleString()}</span>
          <span className="text-xs text-gray-400 w-10 text-right">{site.pct}%</span>
        </button>

        {expanded && site.urls && (
          <div className="ml-10 mr-2 mb-2 bg-white rounded-lg border border-gray-100 overflow-hidden" data-testid={`referral-urls-${site.site.replace(/[/.]/g, '-')}`}>
            {site.urls.map((u, ui) => (
              <button
                key={u.url}
                onClick={() => setSelectedUrl(u)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-orange-50/40 ${
                  ui < site.urls.length - 1 ? 'border-b border-gray-50' : ''
                }`}
                data-testid={`referral-url-${ui}`}
              >
                <ExternalLink size={9} className="text-gray-300 flex-shrink-0" />
                <span className="text-[11px] text-gray-700 flex-1 truncate font-medium">{u.url}</span>
                <span className="text-[10px] font-bold text-gray-900 w-10 text-right">{u.clicks.toLocaleString()}</span>
                <span className="text-[10px] text-gray-400 w-16 text-right hidden sm:block">{u.bounceRate}% br</span>
                <ChevronRight size={10} className="text-gray-300" />
              </button>
            ))}
          </div>
        )}
      </div>
      {selectedUrl && <UrlDetailPopup urlData={selectedUrl} onClose={() => setSelectedUrl(null)} />}
    </>
  );
};

/* ── Main Analytics Widget ── */
const AnalyticsWidget = () => {
  const [range, setRange] = useState(30);
  const [chartMetric, setChartMetric] = useState('sessions');
  const [region, setRegion] = useState('global');

  const regionScale = getRegionMultiplier(region);

  const dailyData = useMemo(() => {
    const raw = generateDailyData(range);
    if (regionScale === 1) return raw;
    return raw.map(d => ({
      ...d,
      visitors: Math.max(1, Math.round(d.visitors * regionScale)),
      sessions: Math.max(1, Math.round(d.sessions * regionScale)),
      pageViews: Math.max(1, Math.round(d.pageViews * regionScale)),
    }));
  }, [range, regionScale]);

  const totals = useMemo(() => {
    const t = dailyData.reduce((acc, d) => ({
      visitors: acc.visitors + d.visitors,
      sessions: acc.sessions + d.sessions,
      pageViews: acc.pageViews + d.pageViews,
    }), { visitors: 0, sessions: 0, pageViews: 0 });
    return {
      ...t,
      avgDuration: '2m 34s',
      bounceRate: 42.3,
      pagesPerSession: (t.pageViews / t.sessions).toFixed(1),
    };
  }, [dailyData]);

  const topPages = useMemo(() => {
    const raw = getTopPages(range);
    if (regionScale === 1) return raw;
    return raw.map(p => ({ ...p, views: Math.max(1, Math.round(p.views * regionScale)) }));
  }, [range, regionScale]);
  const trafficSources = useMemo(() => getTrafficSources(range), [range]);
  const devices = useMemo(() => getDevices(range), [range]);
  const countries = useMemo(() => getCountries(range), [range]);
  const searchKeywords = useMemo(() => {
    const raw = getSearchKeywords(range);
    if (regionScale === 1) return raw;
    return raw.map(k => ({
      ...k,
      clicks: Math.max(1, Math.round(k.clicks * regionScale)),
      impressions: Math.max(1, Math.round(k.impressions * regionScale)),
    }));
  }, [range, regionScale]);
  const searchTotals = useMemo(() => ({
    clicks: searchKeywords.reduce((a, k) => a + k.clicks, 0),
    impressions: searchKeywords.reduce((a, k) => a + k.impressions, 0),
  }), [searchKeywords]);
  const searchEngines = useMemo(() => getSearchEngines(range), [range]);
  const referralSites = useMemo(() => {
    const raw = getReferralSites(range);
    if (regionScale === 1) return raw;
    return raw.map(r => ({ ...r, sessions: Math.max(1, Math.round(r.sessions * regionScale)) }));
  }, [range, regionScale]);

  const RANGES = [
    { label: '7 Days', value: 7 },
    { label: '30 Days', value: 30 },
    { label: '90 Days', value: 90 },
    { label: 'All Time', value: 365 },
  ];

  const rangeLabel = RANGES.find(r => r.value === range)?.label || '30 Days';

  const regionLabel = useMemo(() => getRegionLabel(region), [region]);

  return (
    <div data-testid="analytics-widget">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h3 className="text-base sm:text-lg font-black text-gray-900">Website Analytics</h3>
          <p className="text-xs text-gray-500 mt-0.5">Property ID: 389579887 · Tracking ID: G-YB6NL9W4WG</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <RegionSelector
            region={region}
            onRegionChange={setRegion}
          />
          <div className="flex bg-gray-100 rounded-lg p-0.5" data-testid="date-range-selector">
            {RANGES.map(r => (
              <button
                key={r.value}
                onClick={() => setRange(r.value)}
                className={`px-2 sm:px-3 py-1.5 text-[10px] sm:text-xs font-bold rounded-md transition-all ${
                  range === r.value
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <span className="px-2.5 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">LIVE</span>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 mb-4 sm:mb-6" data-testid="kpi-cards">
        <KpiCard label="Sessions" value={totals.sessions.toLocaleString()} change={12.4} icon={Users} />
        <KpiCard label="Page Views" value={totals.pageViews.toLocaleString()} change={8.7} icon={Eye} />
        <KpiCard label="Unique Visitors" value={totals.visitors.toLocaleString()} change={15.2} icon={TrendingUp} />
        <KpiCard label="Avg. Duration" value={totals.avgDuration} change={3.1} icon={Clock} />
        <KpiCard label="Bounce Rate" value={totals.bounceRate} change={-2.8} suffix="%" icon={ArrowUpRight} />
      </div>

      {/* Traffic Trend Chart */}
      <div className="bg-white border border-gray-100 rounded-xl p-3 sm:p-5 mb-4 sm:mb-6" data-testid="traffic-chart">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-black text-gray-900 uppercase tracking-widest">Traffic Overview</h4>
            <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{rangeLabel}</span>
            {region !== 'global' && <span className="text-[10px] font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded">{regionLabel}</span>}
          </div>
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            {[
              { key: 'sessions', label: 'Sessions' },
              { key: 'pageViews', label: 'Views' },
              { key: 'visitors', label: 'Visitors' },
            ].map(m => (
              <button
                key={m.key}
                onClick={() => setChartMetric(m.key)}
                className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${
                  chartMetric === m.key
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={dailyData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="colorMetric" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#E8520A" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#E8520A" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={{ stroke: '#f0f0f0' }}
                interval={range <= 7 ? 0 : range <= 30 ? 4 : range <= 90 ? 13 : 30}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip content={<ChartTooltip />} />
              <Area
                type="monotone"
                dataKey={chartMetric}
                stroke="#E8520A"
                strokeWidth={2}
                fill="url(#colorMetric)"
                name={chartMetric === 'sessions' ? 'Sessions' : chartMetric === 'pageViews' ? 'Page Views' : 'Visitors'}
                dot={range <= 7}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Two-column: Top Pages + Traffic Sources */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6 mb-4 sm:mb-6">
        {/* Top Pages - 3 cols */}
        <div className="lg:col-span-3 bg-white border border-gray-100 rounded-xl p-3 sm:p-5" data-testid="top-pages">
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-sm font-black text-gray-900 uppercase tracking-widest">Top Pages</h4>
            <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{rangeLabel}</span>
            {region !== 'global' && <span className="text-[10px] font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded">{regionLabel}</span>}
          </div>
          <div className="space-y-2">
            {topPages.map((pg, i) => (
              <div key={pg.page} className="flex items-center gap-3 group">
                <span className="w-5 text-xs font-bold text-gray-300 text-right">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900 truncate group-hover:text-orange-600 transition-colors">{pg.title}</span>
                    <span className="text-xs text-gray-400 hidden sm:inline">{pg.page}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1">
                    <div
                      className="h-1.5 rounded-full transition-all"
                      style={{ width: `${pg.pct}%`, backgroundColor: '#E8520A', opacity: 1 - i * 0.08 }}
                    />
                  </div>
                </div>
                <span className="text-xs font-bold text-gray-500 w-14 text-right">{pg.views.toLocaleString()}</span>
                <span className="text-xs text-gray-400 w-12 text-right">{pg.pct}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Traffic Sources - 2 cols */}
        <div className="lg:col-span-2 bg-white border border-gray-100 rounded-xl p-3 sm:p-5" data-testid="traffic-sources">
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-sm font-black text-gray-900 uppercase tracking-widest">Traffic Sources</h4>
            <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{rangeLabel}</span>
          </div>
          <div className="flex items-center justify-center mb-4" style={{ height: 160 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={trafficSources}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={70}
                  paddingAngle={3}
                  dataKey="value"
                  stroke="none"
                >
                  {trafficSources.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => `${value}%`}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2">
            {trafficSources.map(s => {
              const Icon = SOURCE_ICONS[s.name] || Globe;
              return (
                <div key={s.name} className="flex items-center gap-2.5">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                  <Icon size={13} className="text-gray-400 flex-shrink-0" />
                  <span className="text-xs text-gray-700 flex-1">{s.name}</span>
                  <span className="text-xs font-bold text-gray-900">{s.value}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Two-column: Devices + Geography */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
        {/* Device Breakdown */}
        <div className="bg-white border border-gray-100 rounded-xl p-3 sm:p-5" data-testid="device-breakdown">
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-sm font-black text-gray-900 uppercase tracking-widest">Devices</h4>
            <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{rangeLabel}</span>
          </div>
          <div className="space-y-3">
            {devices.map(d => {
              const Icon = d.icon;
              return (
                <div key={d.name} className="flex items-center gap-3">
                  <Icon size={18} style={{ color: d.color }} className="flex-shrink-0" />
                  <span className="text-sm font-semibold text-gray-700 w-16">{d.name}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-2.5">
                    <div
                      className="h-2.5 rounded-full transition-all"
                      style={{ width: `${d.value}%`, backgroundColor: d.color }}
                    />
                  </div>
                  <span className="text-sm font-black text-gray-900 w-10 text-right">{d.value}%</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Geography */}
        <div className="bg-white border border-gray-100 rounded-xl p-3 sm:p-5" data-testid="geography">
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-sm font-black text-gray-900 uppercase tracking-widest">Top Regions</h4>
            <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{rangeLabel}</span>
          </div>
          <div className="space-y-2.5">
            {countries.map(c => (
              <div key={c.name} className="flex items-center gap-3">
                <span className="text-base w-6 text-center">{c.flag}</span>
                <span className="text-sm text-gray-700 flex-1">{c.name}</span>
                <span className="text-xs text-gray-500 w-14 text-right">{c.sessions.toLocaleString()}</span>
                <span className="text-xs font-bold text-gray-900 w-12 text-right">{c.pct}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Search & Keywords Analysis ── */}
      <div className="bg-white border border-gray-100 rounded-xl p-3 sm:p-5 mb-4 sm:mb-6" data-testid="search-keywords">
        <div className="flex items-center gap-2 mb-5">
          <Search size={16} style={{ color: '#E8520A' }} />
          <h4 className="text-sm font-black text-gray-900 uppercase tracking-widest">Search & Keywords Analysis</h4>
          <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{rangeLabel}</span>
          {region !== 'global' && <span className="text-[10px] font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded">{regionLabel}</span>}
        </div>

        {/* Keywords Table */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 size={14} className="text-gray-400" />
            <h5 className="text-xs font-bold text-gray-500 uppercase tracking-widest">Top Search Queries</h5>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="keywords-table">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-[10px] font-bold uppercase tracking-widest text-gray-400 pb-2 pr-4">#</th>
                  <th className="text-[10px] font-bold uppercase tracking-widest text-gray-400 pb-2 pr-4">Keyword</th>
                  <th className="text-[10px] font-bold uppercase tracking-widest text-gray-400 pb-2 pr-4 text-right">Clicks</th>
                  <th className="text-[10px] font-bold uppercase tracking-widest text-gray-400 pb-2 pr-2 text-right">Trend</th>
                  <th className="text-[10px] font-bold uppercase tracking-widest text-gray-400 pb-2 pr-4 text-right hidden sm:table-cell">Impressions</th>
                  <th className="text-[10px] font-bold uppercase tracking-widest text-gray-400 pb-2 pr-4 text-right">CTR</th>
                  <th className="text-[10px] font-bold uppercase tracking-widest text-gray-400 pb-2 text-right hidden sm:table-cell">Avg. Pos.</th>
                </tr>
              </thead>
              <tbody>
                {searchKeywords.map((kw, i) => (
                  <tr key={kw.keyword} className="border-b border-gray-50 hover:bg-orange-50/30 transition-colors">
                    <td className="py-2.5 pr-4 text-xs font-bold text-gray-300">{i + 1}</td>
                    <td className="py-2.5 pr-4">
                      <div className="flex items-center gap-2">
                        <Search size={11} className="text-gray-300 flex-shrink-0" />
                        <span className="text-sm font-semibold text-gray-800">{kw.keyword}</span>
                      </div>
                    </td>
                    <td className="py-2.5 pr-4 text-right">
                      <span className="text-sm font-black text-gray-900">{kw.clicks.toLocaleString()}</span>
                    </td>
                    <td className="py-2.5 pr-2 text-right">
                      <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                        kw.change > 0 ? 'bg-green-50 text-green-700' : kw.change < 0 ? 'bg-red-50 text-red-600' : 'bg-gray-50 text-gray-500'
                      }`} data-testid={`trend-${kw.keyword.replace(/\s/g, '-')}`}>
                        {kw.change > 0 ? <ArrowUp size={9} /> : kw.change < 0 ? <ArrowDown size={9} /> : <Minus size={9} />}
                        {Math.abs(kw.change)}%
                      </span>
                    </td>
                    <td className="py-2.5 pr-4 text-right hidden sm:table-cell">
                      <span className="text-xs text-gray-500">{kw.impressions.toLocaleString()}</span>
                    </td>
                    <td className="py-2.5 pr-4 text-right">
                      <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${kw.ctr >= 5 ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-600'}`}>
                        {kw.ctr}%
                      </span>
                    </td>
                    <td className="py-2.5 text-right hidden sm:table-cell">
                      <span className="text-xs font-semibold text-gray-600">{kw.position.toFixed(1)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-50">
            <MousePointerClick size={12} className="text-gray-400" />
            <span className="text-[10px] text-gray-400">
              Total clicks: <strong className="text-gray-600">{searchTotals.clicks.toLocaleString()}</strong>
              {' · '}Total impressions: <strong className="text-gray-600">{searchTotals.impressions.toLocaleString()}</strong>
            </span>
          </div>
        </div>

        {/* Two-column: Search Engines + Top Referrals */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Search Engines */}
          <div className="bg-gray-50 rounded-xl p-3 sm:p-4" data-testid="search-engines">
            <div className="flex items-center gap-2 mb-4">
              <Globe size={14} className="text-gray-400" />
              <h5 className="text-xs font-bold text-gray-500 uppercase tracking-widest">Search Engines</h5>
            </div>
            <div className="flex items-center justify-center mb-4" style={{ height: 140 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={searchEngines}
                    cx="50%"
                    cy="50%"
                    innerRadius={38}
                    outerRadius={60}
                    paddingAngle={3}
                    dataKey="value"
                    stroke="none"
                  >
                    {searchEngines.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => `${value}%`}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2">
              {searchEngines.map(s => (
                <div key={s.name} className="flex items-center gap-2.5">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                  <span className="text-xs text-gray-700 flex-1">{s.name}</span>
                  <span className="text-xs font-bold text-gray-900">{s.value}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Top Referral Sites — Expandable */}
          <div className="bg-gray-50 rounded-xl p-3 sm:p-4" data-testid="referral-sites">
            <div className="flex items-center gap-2 mb-4">
              <ExternalLink size={14} className="text-gray-400" />
              <h5 className="text-xs font-bold text-gray-500 uppercase tracking-widest">Top Referral Sites</h5>
            </div>
            <div className="space-y-1">
              {referralSites.map((r, i) => (
                <ReferralSiteRow key={r.site} site={r} index={i} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* GA4 Connection Banner */}
      <div className="bg-gradient-to-r from-zinc-900 to-zinc-800 rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="ga4-banner">
        <div className="flex items-center gap-3">
          <Info size={18} className="text-orange-400 flex-shrink-0" />
          <div>
            <p className="text-sm font-bold text-white">Connect Google Analytics for Live Data</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Currently showing sample metrics. Provide a GA4 service account JSON key to display real-time analytics from property <strong>389579887</strong>.
            </p>
          </div>
        </div>
        <a
          href="https://analytics.google.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex-shrink-0 px-4 py-2 text-xs font-bold text-white rounded-lg transition-all hover:opacity-90"
          style={{ backgroundColor: '#E8520A' }}
        >
          Open Google Analytics
        </a>
      </div>
    </div>
  );
};

export default AnalyticsWidget;
